import * as React from 'react';
import { RankedBars } from '@/components/ui/charts';
import { StatusChip, type StatusChipTone } from '@/components/ui/status-chip';
import type {
  DispositionRow,
  SensitivityCase,
  VendorIntelligence,
} from '@/lib/api/vendor-intelligence';
import { AreaMatrix, type AreaCellRef } from './area-matrix';
import { DispositionList } from './dispositions';
import {
  OUTCOME_LABEL,
  assessedInDepth,
  caseFindings,
  productTitle,
  rankLabel,
  rankedProducts,
  sensitivityLabel,
  sensitivityLine,
} from './vi-logic';

const OUTCOME_TONE: Record<SensitivityCase['outcome'], StatusChipTone> = {
  holds: 'healthy',
  tied: 'amber',
  changes: 'at-risk',
  not_applicable: 'neutral',
};

/**
 * The Shortlist step (E24-F02; T-0272, T-0274, T-0276): the ranking as one
 * bar per vendor with the top three marked, the five sensitivity checks as
 * a table with every vendor's index under each check and what each check
 * found, the deriver's sentence, the dispositions a consultant confirms,
 * and the fit by client area for the vendors assessed in depth.
 *
 * Every figure is the server's (@caliber-2/vendor-fit, confirmed ratings
 * only); nothing here computes one. A tie is shown as a tie: a shared rank
 * ("2=") and "Ties", never "Order changes".
 */
export function ShortlistPart({
  evaluation,
  canEdit,
  busy,
  error,
  selectedCell,
  onOpenCell,
  onConfirmShortlist,
  onSetDisposition,
}: {
  evaluation: VendorIntelligence;
  canEdit: boolean;
  busy: boolean;
  /** The last disposition write's error, shown under the list. */
  error: unknown;
  /** The area matrix cell whose panel is open. */
  selectedCell: AreaCellRef | null;
  onOpenCell: (cell: AreaCellRef) => void;
  onConfirmShortlist: (rows: DispositionRow[]) => void;
  onSetDisposition: (row: DispositionRow) => Promise<unknown>;
}) {
  const { fit } = evaluation;
  const byId = React.useMemo(
    () => new Map(evaluation.products.map((p) => [p.product_id, p])),
    [evaluation.products],
  );
  const nameOf = (id: string) => {
    const p = byId.get(id);
    return p ? `${p.vendor.name} · ${p.name}` : id;
  };
  const shortName = (id: string) => byId.get(id)?.name ?? id;

  if (fit.top.length === 0) {
    return (
      <p className="m-0 rounded-card border border-dashed border-ink-200 px-4 py-3 text-body-sm text-ink-500">
        Confirm ratings to see the shortlist. AI-proposed ratings count once you confirm them.
      </p>
    );
  }

  const ranked = rankedProducts(evaluation);
  const columns = ranked.filter((p) => p.rank !== null);
  const line = sensitivityLine(evaluation);
  const inDepth = assessedInDepth(evaluation).map((p) => {
    const product = byId.get(p.product_id);
    const title = product ? productTitle(product) : { title: p.name, vendor: null };
    return {
      product_id: p.product_id,
      name: title.title,
      vendor: title.vendor,
      rank: rankLabel(p),
      index: p.index,
      coverage: p.coverage,
    };
  });
  return (
    <div className="flex flex-col gap-5">
      <section aria-labelledby="vi-ranking" className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <h2 id="vi-ranking" className="m-0 text-h3 font-semibold text-ink-900">
            Ranking
          </h2>
          <span className="text-meta text-ink-500">
            Fit index out of 100, from confirmed ratings only. The top three are marked.
          </span>
        </div>
        <RankedBars
          caption="Fit index by vendor"
          bars={ranked.map((p) => ({
            key: p.product_id,
            rank: rankLabel(p),
            label: nameOf(p.product_id),
            note:
              p.rank === null
                ? 'No confirmed rating yet'
                : `coverage ${p.coverage.toFixed(1)}% · ${p.confirmed_modules} of ${p.modules} modules confirmed`,
            value: p.rank === null ? null : p.index,
            emphasis: p.top,
          }))}
        />
      </section>

      <section aria-labelledby="vi-sensitivity" className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <h2 id="vi-sensitivity" className="m-0 text-h3 font-semibold text-ink-900">
            Sensitivity
          </h2>
          <span className="text-meta text-ink-500">
            Would the top three change if the sums were made another way? Each vendor column is its
            index under that check.
          </span>
        </div>
        <div className="overflow-x-auto rounded-card border border-ink-100">
          <table className="w-full border-collapse text-body-sm" aria-label="Sensitivity checks">
            <thead>
              <tr className="border-b border-ink-100 text-left text-meta font-medium text-ink-500 align-bottom">
                <th scope="col" className="px-3 py-2 font-medium">
                  Check
                </th>
                {columns.map((p) => (
                  <th
                    key={p.product_id}
                    scope="col"
                    className="whitespace-nowrap px-3 py-2 text-right font-medium"
                  >
                    <span className="block text-ink-900">{shortName(p.product_id)}</span>
                    <span className="block font-mono font-normal tabular-nums">
                      #{rankLabel(p)}
                    </span>
                  </th>
                ))}
                <th scope="col" className="px-3 py-2 font-medium">
                  Top three, in order
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Outcome
                </th>
              </tr>
            </thead>
            <tbody>
              {fit.sensitivity.cases.map((c) => {
                const indexOf = new Map(c.products.map((p) => [p.product_id, p.index]));
                return (
                  <tr key={c.key} className="border-b border-ink-100 align-top last:border-b-0">
                    <th
                      scope="row"
                      className="whitespace-nowrap px-3 py-2 text-left font-normal text-ink-900"
                    >
                      {sensitivityLabel(c)}
                    </th>
                    {columns.map((p) => {
                      const v = c.applicable ? indexOf.get(p.product_id) : undefined;
                      return (
                        <td
                          key={p.product_id}
                          className="px-3 py-2 text-right font-mono tabular-nums text-ink-700"
                        >
                          {v === undefined ? '–' : v.toFixed(1)}
                        </td>
                      );
                    })}
                    <td className="whitespace-nowrap px-3 py-2 text-ink-700">
                      {c.applicable ? c.top.map(shortName).join(' · ') : '–'}
                    </td>
                    <td className="px-3 py-2">
                      <StatusChip tone={OUTCOME_TONE[c.outcome]} size="sm">
                        {OUTCOME_LABEL[c.outcome]}
                      </StatusChip>
                      {c.applicable && c.outcome !== 'holds' ? (
                        <span className="mt-1 block text-meta text-ink-500">
                          {caseFindings(c, shortName).join('; ')}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {line ? <p className="m-0 text-body-sm font-medium text-ink-900">{line}.</p> : null}
      </section>

      <DispositionList
        evaluation={evaluation}
        canEdit={canEdit}
        busy={busy}
        error={error}
        onConfirmAll={onConfirmShortlist}
        onSetOne={onSetDisposition}
      />

      <section aria-labelledby="vi-area-matrix" className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <h2 id="vi-area-matrix" className="m-0 text-h3 font-semibold text-ink-900">
            Fit by client area
          </h2>
          <span className="text-meta text-ink-500">
            The confirmed module ratings rolled up to the client's areas, for the shortlisted and
            alternate vendors. A cell opens the module ratings behind it.
          </span>
        </div>
        <AreaMatrix
          areas={evaluation.areas}
          columns={inDepth}
          notAssessed={evaluation.not_assessed_by_area}
          selected={selectedCell}
          onOpenCell={onOpenCell}
        />
        <p className="m-0 text-meta text-ink-500">
          How an area is rated: each of its requirements counts its module's rating (Strong 1,
          Partial a half, Gap and Unknown 0) at its weight (critical 2, otherwise 1), and the area
          takes the weighted mean: Strong from 0.8, Partial from 0.4, else Gap; Unknown when half or
          more of the weight has no confirmed rating other than Unknown. Requirements with no module
          are left out, and the index is the same whether summed by area or by module.
        </p>
      </section>

      <p className="m-0 text-meta text-ink-500">
        How the index is made: each confirmed rating counts Strong as 1, Partial as a half, Gap and
        Unknown as 0, weighted by the requirements in its module (a total weight of{' '}
        {fit.total_weight}), and the sum is shown out of 100. Coverage is the share of that weight
        with evidence behind it, so a vendor with little evidence is not mistaken for a weak one.
        AI-proposed ratings count once you confirm them.
      </p>
    </div>
  );
}
